import React from 'react';
import { cn } from 'utils';
import { useTimeline } from './TimelineProvider';
import { getScaleTimeIncrement } from './utils';
import { TimelineRulerTick } from './TimelineRulerTick';

/**
 * Groups of time increments that make up the timeline ruler.
 */
export function TimelineTickGroup(props: {
  label: string;
  // Number of ticks in the group
  numTicks?: number;
  // Position of the group in the overall timeline as a decimal (0 to 1)
  position: number;
  // Represents the size of the group in the overall timeline as a decimal (0 to 1)
  scaledSize: number;
  // Timestamp of the first tick
  start: number;
}): React.ReactElement {
  const { label, numTicks = 4, position = 0, scaledSize, start } = props;

  const { scale } = useTimeline();

  const ticks = React.useMemo(
    () =>
      Array.from({ length: numTicks }, (_, i) => ({
        label: `${label} ${i + 1}`,
        major: i === 0,
        position: i / numTicks,
        time: start + getScaleTimeIncrement(scale) * i,
      })),
    [scale, start, numTicks],
  );

  return (
    <div
      className={cn([
        'odin-absolute',
        'odin-flex',
        'odin-h-full',
        'odin-items-end',
        'odin-left-0',
        'odin-overflow-hidden',
        'odin-top-0',
      ])}
      style={{ left: `${position * 100}%`, width: `${scaledSize * 100}%` }}
    >
      {label && (
        <strong
          className={cn([
            'odin-absolute',
            'odin-font-normal',
            'odin-left-2',
            'odin-text-slate-500',
            'odin-text-xs',
            'odin-top-0',
          ])}
        >
          {label}
        </strong>
      )}
      {ticks.map((tick) => (
        <TimelineRulerTick key={`timeline-tick-${tick.time}`} {...tick} />
      ))}
    </div>
  );
}
